import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2, Users, Shield, type LucideIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";
import { getOrganizationVoices } from "../settings/settings-api";
import { whatsappApi } from "../admin/whatsapp-api";
import { useSettings } from "../settings/use-settings";
import { GeneralTab } from "./general-tab";
import { UsersTab, type OwnerAccessControls } from "./users-tab";
import { useInvitations } from "./use-invitations";
import { RolesTab } from "./roles-tab";

const SECTIONS: { id: string; label: string; icon: LucideIcon }[] = [
  { id: "general", label: "General", icon: Building2 },
  { id: "users", label: "Users", icon: Users },
  { id: "roles", label: "Roles", icon: Shield },
];
const SECTION_IDS = new Set(SECTIONS.map(s => s.id));

export function OrganisationPage() {
  const s = useSettings();
  const { account, token, roleId, user, features, organization } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get("section");
  const [section, setSectionState] = useState(
    initial && SECTION_IDS.has(initial) ? initial : "general",
  );

  const setSection = (next: string) => {
    setSectionState(next);
    setSearchParams(next === "general" ? {} : { section: next }, { replace: true });
  };

  const orgId = account?.organization_id;
  // Owner-only on the server, so don't ask as a member: the request would 403 and the
  // toggles it feeds would fail on click anyway.
  const isOrgOwner = roleId === "owner";
  const orgVoices = useQuery({
    queryKey: ["organisation", "voices", orgId],
    queryFn: () => getOrganizationVoices(orgId as string, token as string),
    enabled: !!orgId && !!token && isOrgOwner && section === "users",
    staleTime: 60_000,
    retry: false,
  });
  // Full number inventory for the WhatsApp access picker. Owner-only on the
  // server like voices above; owners are never grant-restricted so this is
  // always the whole account list.
  const clientId = user?.client_id || (user as any)?.id;
  const orgInstances = useQuery({
    queryKey: ["organisation", "whatsapp-instances", orgId],
    queryFn: () => whatsappApi.listForClient(token as string, clientId as string),
    enabled: !!orgId && !!token && isOrgOwner && section === "users",
    staleTime: 60_000,
    retry: false,
  });

  // The R3 owner routes are owner-only and 404 while their switch is off, so neither the
  // request nor the UI exists unless core reports both.
  const invitesOn = isOrgOwner && features?.org_invitations === true;
  const grantsOn = isOrgOwner && features?.member_grants === true;
  const invitations = useInvitations({
    orgId, token, enabled: invitesOn && section === "users", onChanged: () => { void s.reloadMembers(); },
  });
  const owner: OwnerAccessControls | undefined = invitesOn || grantsOn
    ? {
        invitations: invitesOn ? invitations : null,
        memberGrants: grantsOn,
        entitlements: organization?.entitlements,
        memberScope: features.member_scope,
        onInvite: (email, role, access) => s.inviteMember(email, role, access),
        onSaveAccess: s.saveMemberAccess,
        onStatus: s.setMemberStatus,
      }
    : undefined;

  return (
    <div className="space-y-4 px-4 sm:px-5 md:px-6 pb-6 pt-3 sm:pt-4">
      <div>
        <h1 className="text-sm font-semibold text-ink">Organisation</h1>
        
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Section nav */}
        <nav className="h-fit rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-1.5 lg:sticky lg:top-8">
          <div className="px-3 py-2 text-xs font-semibold text-ink-3 uppercase tracking-wider mb-1">Select Menu</div>
          {SECTIONS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setSection(id)}
              className={cn(
                "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
                section === id ? "bg-accent-50 font-semibold text-accent-700 dark:bg-accent-500/10 dark:text-accent-400" : "text-ink-2 hover:bg-soft hover:text-ink",
              )}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="lg:col-span-3">
          {section === "general" && (
            <GeneralTab
              orgName={s.orgName} orgTimezone={s.orgTimezone}
              onSave={(name, tz) => { s.setOrgName(name); s.setOrgTimezone(tz); }}
            />
          )}
          {section === "users" && (
            <UsersTab
              members={s.members}
              user={user}
              canManageVoices={isOrgOwner}
              orgVoices={orgVoices.data?.voices ?? []}
              onRole={s.setMemberRole}
              onRemove={s.removeMember}
              onInvite={s.inviteMember}
              onPermissions={s.setMemberPermissions}
              onVoices={s.setMemberVoices}
              orgInstances={(orgInstances.data ?? []).map(c => ({
                instance_name: c.instance_name,
                phone_number: c.phone_number,
              }))}
              orgInstancesError={orgInstances.isError ? "Could not load WhatsApp numbers. Retry." : null}
              onWhatsAppInstances={s.setMemberWhatsAppInstances}
              owner={owner}
            />
          )}
          {section === "roles" && (
            <RolesTab />
          )}
        </div>
      </div>
    </div>
  );
}
